import React from "react";
import { redirect } from "react-router-dom";

import jwt from "jsonwebtoken";
import FormAlert from "../components/FormAlert";
import { Breakpoint } from "../hooks/useBreakpoint";
import { ILocalStorageUserData } from "../types/users";
import { IAlertParams } from "../types/shared";

export const getTokenFromLocalStorage = (): string | null => {
    const userDataRaw = localStorage.getItem("userData");

    if (userDataRaw) {
        const userData: ILocalStorageUserData = JSON.parse(userDataRaw);
        return userData.token;
    } else {
        return null;
    }
};

export const getUserFromLocalStorage = ():
    | ILocalStorageUserData["user"]
    | null => {
    const userDataRaw = localStorage.getItem("userData");

    if (userDataRaw) {
        const userData: ILocalStorageUserData = JSON.parse(userDataRaw);
        return userData.user;
    } else {
        return null;
    }
};

export const validateToken = (token: string): boolean => {
    try {
        jwt.verify(token, process.env.JWT_SECRET as string);
        return true;
    } catch (error) {
        return false;
    }
};

const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
};

export const isLoggedIn = (): boolean => {
    let isValid = false;
    const token = getTokenFromLocalStorage();

    if (token) {
        isValid = validateToken(token);
    }

    if (isValid) {
        return true;
    } else {
        const refreshToken = getCookie("majorybl_refresh_token");

        if (refreshToken) {
            isValid = validateToken(refreshToken);
        }
    }

    return isValid;
};

export const logout = () => {
    // TODO send call to tokenBlacklist API function
    localStorage.removeItem("userData");
    return redirect("/login");
};

export const displayFormAlert = (params: IAlertParams): React.JSX.Element => (
    <FormAlert {...params} />
);

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "instant",
    });
};

export const defaultDateTimeFormat = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
});

export const defaultDateFormat = new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
});

export const defaultTimeFormat = new Intl.DateTimeFormat("en-US", {
    timeStyle: "short",
});

export const currencyFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
});

export const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const serializeForm = (formData: FormData) =>
    JSON.stringify(
        Array.from(formData.entries()).reduce(
            (m, [key, value]) => Object.assign(m, { [key]: value }),
            {},
        ),
    );

export const debounce = (callback: any, wait: number) => {
    let timeoutId: number | undefined = undefined;
    return (...args: any) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback.apply(null, args);
        }, wait);
    };
};

export const isXOrBigger = (x: Breakpoint, breakpoint: Breakpoint): boolean => {
    const breakpointsOrder: Record<Breakpoint, number> = {
        xs: 0,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4,
        xxl: 5,
    };

    return breakpointsOrder[breakpoint] >= breakpointsOrder[x];
};

export const isXOrSmaller = (
    x: Breakpoint,
    breakpoint: Breakpoint,
): boolean => {
    const breakpointsOrder: Record<Breakpoint, number> = {
        xs: 0,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4,
        xxl: 5,
    };

    return breakpointsOrder[breakpoint] <= breakpointsOrder[x];
};

export const isValidDate = (d: string) => {
    const date = new Date(d);
    return !isNaN(date.getTime());
};

export function ordinalSuffix(number: number): string {
    if (number > 3 && number < 21) return "th";
    switch (number % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}
